/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Modal, Form, Badge, Alert, Spinner, Accordion } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';

const DispatcherManagement = () => {
  const [dispatchers, setDispatchers] = useState([]);
  const [dispatcherFleets, setDispatcherFleets] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [assignForm, setAssignForm] = useState({
    dispatcherId: '',
    fleetName: '',
    dispatcherNotes: ''
  });

  useEffect(() => {
    fetchDispatcherData();
  }, []);

  const fetchDispatcherData = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get('/admin/dispatchers');
      setDispatchers(data.dispatchers);
      setDispatcherFleets(data.dispatcherFleets);
    } catch (error) {
      console.error('Error fetching dispatcher data:', error);
      toast.error('Failed to fetch dispatcher data');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignDriver = (driver) => {
    setSelectedDriver(driver);
    setAssignForm({
      dispatcherId: driver.assignedDispatcher?._id || '',
      fleetName: driver.fleetName || '',
      dispatcherNotes: driver.dispatcherNotes || ''
    });
    setShowAssignModal(true);
  };

  const handleSaveAssignment = async () => {
    try {
      await axios.post('/admin/dispatchers/assign-driver', {
        driverId: selectedDriver._id,
        dispatcherId: assignForm.dispatcherId || null,
        fleetName: assignForm.fleetName,
        dispatcherNotes: assignForm.dispatcherNotes
      });

      toast.success('Driver assignment updated successfully');
      setShowAssignModal(false);
      fetchDispatcherData();
    } catch (error) {
      console.error('Error assigning driver:', error);
      toast.error('Failed to update driver assignment');
    }
  };

  const getDispatcherName = (dispatcherId) => {
    if (!dispatcherId) return 'Unassigned';
    const dispatcher = dispatchers.find(d => d._id === dispatcherId);
    return dispatcher ? dispatcher.name : 'Unknown';
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center p-4">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>🚛 Dispatcher Fleet Management</h2>
        <Button variant="primary" onClick={fetchDispatcherData}>
          🔄 Refresh
        </Button>
      </div>

      <Row>
        {/* Dispatchers Overview */}
        <Col md={4}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Dispatchers ({dispatchers.length})</h5>
            </Card.Header>
            <Card.Body>
              {dispatchers.map(dispatcher => {
                const fleet = dispatcherFleets[dispatcher._id];
                const driverCount = fleet ? fleet.drivers.length : 0;
                
                return (
                  <div key={dispatcher._id} className="mb-3 p-3 border rounded">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="mb-1">{dispatcher.name}</h6>
                        <small className="text-muted">{dispatcher.email}</small>
                      </div>
                      <Badge bg="primary">{driverCount} drivers</Badge>
                    </div>
                    {dispatcher.companyName && (
                      <small className="text-muted d-block">{dispatcher.companyName}</small>
                    )}
                  </div>
                );
              })}
            </Card.Body>
          </Card>
        </Col>

        {/* Fleet Assignments */}
        <Col md={8}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Driver Fleet Assignments</h5>
            </Card.Header>
            <Card.Body>
              {Object.entries(dispatcherFleets).map(([key, fleet]) => (
                <div key={key} className="mb-4">
                  <h6 className="text-primary">
                    {fleet.dispatcher ? fleet.dispatcher.name : 'Unassigned Drivers'}
                    <Badge bg="secondary" className="ms-2">{fleet.drivers.length}</Badge>
                  </h6>
                  
                  {fleet.drivers.length > 0 ? (
                    <Accordion flush>
                      {fleet.drivers.map((driver, index) => (
                        <Accordion.Item eventKey={`${key}-${index}`} key={driver._id}>
                          <Accordion.Header>
                            <div className="d-flex justify-content-between align-items-center w-100 pe-3">
                              <div>
                                <strong>{driver.name}</strong>
                                <br />
                                <small className="text-muted">{driver.companyName || 'N/A'}</small>
                              </div>
                              <Badge bg={driver.subscriptionStatus === 'active' ? 'success' : 'danger'}>
                                {driver.subscriptionStatus === 'active' ? 'Active' : (driver.subscriptionStatus || 'Inactive')}
                              </Badge>
                            </div>
                          </Accordion.Header>
                          <Accordion.Body>
                            <div className="mb-2">
                              <strong>Email:</strong> {driver.email}
                            </div>
                            <div className="mb-2">
                              <strong>Company:</strong> {driver.companyName || 'N/A'}
                            </div>
                            <div className="mb-2">
                              <strong>Fleet Name:</strong>{' '}
                              {driver.fleetName ? (
                                <Badge bg="info">{driver.fleetName}</Badge>
                              ) : (
                                <span className="text-muted">No fleet name</span>
                              )}
                            </div>
                            <div className="mb-2">
                              <strong>Dispatch Email:</strong>{' '}
                              {driver.dispatchEmail ? (
                                <a href={`mailto:${driver.dispatchEmail}`} className="text-decoration-none">
                                  {driver.dispatchEmail}
                                </a>
                              ) : (
                                <span className="text-muted">Not set</span>
                              )}
                            </div>
                            <div className="mb-3">
                              <strong>Notes:</strong>{' '}
                              {driver.dispatcherNotes ? (
                                <div className="mt-1">{driver.dispatcherNotes}</div>
                              ) : (
                                <span className="text-muted">No notes</span>
                              )}
                            </div>
                            <Button 
                              variant="primary" 
                              size="sm"
                              onClick={() => handleAssignDriver(driver)}
                              className="w-100"
                            >
                              ✏️ Edit Assignment
                            </Button>
                          </Accordion.Body>
                        </Accordion.Item>
                      ))}
                    </Accordion>
                  ) : (
                    <Alert variant="info">
                      No drivers assigned to this dispatcher.
                    </Alert>
                  )}
                </div>
              ))}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Assignment Modal */}
      <Modal show={showAssignModal} onHide={() => setShowAssignModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Assign Driver to Dispatcher</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedDriver && (
            <div className="mb-3">
              <h6>Driver: {selectedDriver.name}</h6>
              <p className="text-muted">{selectedDriver.email} • {selectedDriver.companyName}</p>
            </div>
          )}

          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Assign to Dispatcher</Form.Label>
              <Form.Select
                value={assignForm.dispatcherId}
                onChange={(e) => setAssignForm(prev => ({ ...prev, dispatcherId: e.target.value }))}
              >
                <option value="">Unassigned</option>
                {dispatchers.map(dispatcher => (
                  <option key={dispatcher._id} value={dispatcher._id}>
                    {dispatcher.name} ({dispatcher.email})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Fleet Name (Optional)</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., North Fleet, City Center, Airport Express"
                value={assignForm.fleetName}
                onChange={(e) => setAssignForm(prev => ({ ...prev, fleetName: e.target.value }))}
              />
              <Form.Text className="text-muted">
                Custom name for this driver's fleet group
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Dispatcher Notes (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Notes about this driver for the dispatcher..."
                value={assignForm.dispatcherNotes}
                onChange={(e) => setAssignForm(prev => ({ ...prev, dispatcherNotes: e.target.value }))}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAssignModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveAssignment}>
            Save Assignment
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DispatcherManagement;
